import { StatisticsReportDetailsDialog } from './details/statistics-report-details-dialog';
import { DialogService } from 'aurelia-dialog';
import { ServiceReportService } from './../../services/service-report-service';
import { ServiceStationService } from './../../services/service-station-service';
import { ErrorService } from 'services/error-service';
import { UserService } from './../../services/user-service';
import { ServiceService } from 'services/service-service';
import { autoinject, observable } from 'aurelia-framework';
import * as moment from 'moment';
import { Router } from 'aurelia-router';
import { I18N } from 'aurelia-i18n';
import { ServiceStatisticsData } from 'models/service-report-models';
import { Models } from 'models/core';

@autoinject
export class ServiceStatistics {
    @observable({ changeHandler: 'updateStatistics' }) dateFrom: Date;
    @observable({ changeHandler: 'updateStatistics' }) dateTo: Date;

    private selectedStationId: number;
    private selectedStationsIds: number[] = [];
    private disableServicestationDropdown: boolean = true;
    private statisticsData: Array<ServiceStatisticsData>;
    private statisticsSummary: ServiceStatisticsData;
    private serviceStations: Array<Models.ServiceStation> = [];

    private exportReportProgress: boolean = false;

    private hasLoadedData = false;

    constructor(
        private serviceReportService: ServiceReportService,
        private userService: UserService,
        private serviceService: ServiceService,
        private serviceStationService: ServiceStationService,
        private dialogService: DialogService,
        private errorService: ErrorService,
        private router: Router,
        private i18n: I18N
      ) {

        const storedParams = sessionStorage.getItem('service-statistics');
        const parsedParams = storedParams ? JSON.parse(storedParams) : null;

        this.getServiceStations().then(_ => {
          this.userService.getCurrentUser()
          .then(user => {
              // build default filters for statistics
              if(user.ServiceStationId) {
                this.selectedStationId = user.ServiceStationId;
                this.selectedStationsIds = [ user.ServiceStationId ];
              }

              this.dateFrom = parsedParams?.from ?? moment.default().add(-1, 'months').toDate();
              this.dateTo = parsedParams?.to ?? moment.default().toDate();

              // the select2 component does not properly support arrays as the selected value
              // while it will display all the items in the array, the first time a new item is selected they will be discarded
              // the items will also not be highlighted in the dropdown
              // if(parsedParams?.selectedStationsIds) {
              //   this.selectedStationId = parsedParams.selectedStationsIds;
              //   this.selectedStationsIds = parsedParams.selectedStationsIds;
              // }

              this.getStatistics();
              this.disableServicestationDropdown = false;
          });
        });
    }

    getServiceStations() {
        return this.serviceStationService
            .getAllCached()
            .then(res => {
                res = res.filter(x => !x.IsDeleted)
                this.serviceStations = res;
            });
    }

    updateStatistics() {
      if (!this.hasLoadedData) {
        return;
      }
      this.getStatistics();
      sessionStorage.setItem('service-statistics', JSON.stringify({from: this.dateFrom, to: this.dateTo, selectedStationsIds: this.selectedStationsIds }));
    }

    getStatistics() {
        this.serviceReportService.getServiceStatistics(this.selectedStationsIds, this.dateFrom, this.dateTo, false)
            .then(res => {
                this.statisticsData = res.Data ?? [];
                this.statisticsSummary = res.SummaryData ?? null;
                this.hasLoadedData = true;
            })
            .catch(err => this.errorService.handleError(err));
    }

    selectServiceStations(values) {
      if(values) {
        const newValues = values.filter(x => x.id).map(x => Number.parseInt(x.id));

        // Prevent multiple update calls
        if(JSON.stringify(this.selectedStationsIds) !== JSON.stringify(newValues)) {
          this.selectedStationsIds = newValues;
          this.updateStatistics();
        }
      }
    }

    private viewStatisticsInServices(row, rowData, task) {

      if (!rowData) {
        return;
      }

      const { ServiceStationId, FromDate, ToDate } = row;

      if (task == 'hours') {
        const params =
        {
            serviceStationId: ServiceStationId,
            dateFrom: this.dateFrom,
            dateTo: this.dateTo,
        };

        // show dialog with details
        this.dialogService.open({ viewModel: StatisticsReportDetailsDialog, model: params, lock: false, position: (mc, mo) => {} })
            .whenClosed(x => {
                document.querySelector('html').style.overflowY = null;
            }).catch((err) => {
                document.querySelector('html').style.overflowY = null;
                this.errorService.handleError(err);
            });

        return;
      }

      const context = 'statistics';
      const externalFilters = {};
      const localStorageFiltersKey = `${this.serviceService.SERVICE_FILTERS_KEY}_${context}`;
      const localStorageFiltersKeyExternal = `${this.serviceService.SERVICE_FILTERS_KEY}_${context}_EXTERNAL`;

      const data: any = { ...externalFilters };

      switch (task) {

        case 'received':
          data.receivedDate = {
            values: {
              from: FromDate,
              to: ToDate
            },
            visible: true
          };
          data.serviceStationId = ServiceStationId;
          break;

        case 'washed':
          data.washedDate = {
            values: {
              from: FromDate,
              to: ToDate
            },
            visible: true
          };
          data.washingServiceStationId = ServiceStationId;
          break;

        case 'tested':
          data.testedDate = {
            values: {
              from: FromDate,
              to: ToDate
            },
            visible: true
          };
          data.testingServiceStationId = ServiceStationId;
          break;

        case 'repaired':
          data.repairedDate = {
            values: {
              from: FromDate,
              to: ToDate
            },
            visible: true
          };
          data.repairServiceStationId = ServiceStationId;
          break;



        case 'antifouled':
          data.antifouledDate = {
            values: {
              from: FromDate,
              to: ToDate
            },
            visible: true
          };
          data.antifoulingServiceStationId = ServiceStationId;
          break;
        case 'delivered':
          data.deliveredDate = {
            values: {
              from: FromDate,
              to: ToDate
            },
            visible: true
          };
          data.deliveryServiceStationId = ServiceStationId;
          break;
      }

      localStorage.setItem(localStorageFiltersKeyExternal, JSON.stringify(data));
      localStorage.removeItem(localStorageFiltersKey);

      this.router.navigateToRoute('service-list', { context });
    }

    exportReport() {
      this.exportReportProgress = true;

      this.serviceReportService.getServiceStatistics(this.selectedStationsIds, this.dateFrom, this.dateTo, true)
          .then(res => {
              // ignore result, service handles export
              this.exportReportProgress = false;
          })
          .catch(err => {
              this.errorService.handleError(err);
              this.exportReportProgress = false;
          });
  }
}
